import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { User, Award, GraduationCap, Star, HeartHandshake, Mail, ChevronRight, Search, Phone, MapPin, Home, Info } from 'lucide-react';
import { COUNCIL_LEVELS } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toBullets = (text) => {
  if (!text) return [];
  let parts = String(text).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) {
    parts = String(text).split(/[;•]|\.(?=\s|$)/).map((s) => s.trim()).filter(Boolean);
  }
  return parts.map((s) => s.replace(/^[-•]\s*/, ''));
};

const GoverningCouncil = () => {
  const { t } = useLanguage();
  const [level, setLevel] = useState('state');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (heroRef.current) heroRef.current.focus();
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/members`, { params: { level } })
      .then((res) => { if (active) setMembers(res.data || []); })
      .catch(() => { if (active) setMembers([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [level]);

  const isGeneral = level === 'general';
  const q = query.trim().toLowerCase();
  const filtered = q ? members.filter((m) => (m.name || '').toLowerCase().includes(q)) : members;

  return (
    <div className="jbss-app">
      <Header />
      <main className="jbss-council-page">
        <section className="jbss-council-hero" ref={heroRef} tabIndex={-1} data-testid="council-hero">
          <div className="jbss-council-hero-overlay" />
          <div className="jbss-container jbss-council-hero-inner">
            <span className="jbss-eyebrow jbss-eyebrow-light">{t('council.eyebrow')}</span>
            <h1 className="jbss-council-title">{t('council.title')}</h1>
            <p className="jbss-council-sub">{t('council.subtitle')}</p>
          </div>
        </section>

        <section className="jbss-council-section">
          <div className="jbss-container">
            <div className="jbss-level-bar">
              <label className="jbss-level-label" htmlFor="council-level">{t('council.selectLevel')}</label>
              <Select value={level} onValueChange={(v) => { setLevel(v); setQuery(''); }}>
                <SelectTrigger id="council-level" className="jbss-level-select" data-testid="council-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNCIL_LEVELS.map((lv) => (
                    <SelectItem key={lv} value={lv} data-testid={`council-level-option-${lv}`}>
                      {t(`council.levels.${lv}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="jbss-search">
                <Search size={18} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('council.searchPlaceholder')}
                  aria-label={t('council.searchPlaceholder')}
                  data-testid="council-search-input"
                />
              </div>
            </div>

            <h2 className="jbss-level-heading">
              {level === 'general' ? t('council.levels.general') : t('council.membersHeading', { level: t(`council.levels.${level}`) })}
            </h2>

            {loading ? (
              <p className="jbss-no-results" data-testid="council-loading">{t('council.loading')}</p>
            ) : filtered.length === 0 ? (
              <p className="jbss-no-results" data-testid="council-no-results">{t('council.noResults')}</p>
            ) : (
              <ul className="jbss-member-list" data-testid="council-member-list">
                {filtered.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className="jbss-member-row"
                      onClick={() => setSelected(m)}
                      data-testid={`council-member-${m.id}`}
                    >
                      <span className="jbss-member-row-avatar">
                        {m.photo ? <img src={`${API}/photos/${m.photo}`} alt="" /> : <User size={20} />}
                      </span>
                      <span className="jbss-member-row-info">
                        <span className="jbss-member-row-name">{m.name}</span>
                        <span className="jbss-member-row-role">
                          {isGeneral ? (m.location || m.contact_number || '') : (m.designation || '')}
                        </span>
                      </span>
                      <ChevronRight size={18} className="jbss-member-row-chevron" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="jbss-member-dialog jbss-member-dialog-wide" data-testid="council-member-dialog">
          {selected && (
            <div className="jbss-dialog-body">
              <aside className="jbss-dialog-side">
                <div className="jbss-dialog-photo">
                  {selected.photo ? (
                    <>
                      <img className="jbss-dialog-photo-thumb" src={`${API}/photos/${selected.photo}`} alt={selected.name} />
                      <img className="jbss-photo-zoom" src={`${API}/photos/${selected.photo}`} alt="" />
                    </>
                  ) : <User size={52} />}
                </div>
                <span className="jbss-dialog-level">{t(`council.levels.${selected.level}`)}</span>
                <DialogTitle className="jbss-dialog-name">{selected.name}</DialogTitle>
                {!isGeneral && <div className="jbss-dialog-role">{selected.designation}</div>}
                <DialogDescription className="sr-only">
                  {selected.name} — {isGeneral ? t('council.levels.general') : selected.designation}
                </DialogDescription>
                <button className="jbss-member-contact" type="button" data-testid="council-member-contact">
                  <Mail size={14} />
                  <span>{t('council.contactMember')}</span>
                </button>
              </aside>

              <div className="jbss-dialog-details">
                {isGeneral ? (
                  <ul className="jbss-dialog-meta">
                    <li><Phone size={15} /><div><span className="jbss-meta-label">{t('council.contactNumberLabel')}</span><span className="jbss-meta-value">{selected.contact_number || '—'}</span></div></li>
                    <li><MapPin size={15} /><div><span className="jbss-meta-label">{t('council.locationLabel')}</span><span className="jbss-meta-value">{selected.location || '—'}</span></div></li>
                    <li><Mail size={15} /><div><span className="jbss-meta-label">{t('council.emailLabel')}</span><span className="jbss-meta-value">{selected.email || '—'}</span></div></li>
                    <li><Home size={15} /><div><span className="jbss-meta-label">{t('council.placeOfStayLabel')}</span><span className="jbss-meta-value">{selected.place_of_stay || '—'}</span></div></li>
                  </ul>
                ) : (
                  <ul className="jbss-dialog-meta jbss-dialog-meta-bullets">
                    {[
                      { icon: <Info size={15} />, label: t('council.introLabel'), value: selected.intro },
                      { icon: <Star size={15} />, label: t('council.achievementsLabel'), value: selected.achievements },
                      { icon: <GraduationCap size={15} />, label: t('council.qualificationsLabel'), value: selected.qualifications },
                      { icon: <Award size={15} />, label: t('council.awardsLabel'), value: selected.awards },
                      { icon: <HeartHandshake size={15} />, label: t('council.contributionsLabel'), value: selected.contributions },
                    ].map((row, i) => {
                      const bullets = toBullets(row.value);
                      return (
                        <li key={i}>
                          {row.icon}
                          <div>
                            <span className="jbss-meta-label">{row.label}</span>
                            {bullets.length ? (
                              <ul className="jbss-bullets">
                                {bullets.map((b, j) => <li key={j}>{b}</li>)}
                              </ul>
                            ) : (
                              <span className="jbss-meta-value">—</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoverningCouncil;
